import {Checkbox, FormControl} from '@primer/react'
import {BetaLabel} from '@github-ui/lifecycle-labels/beta'

import type {RegisteredRuleSchemaComponent} from '@github-ui/repos-rules-types/rules-types'
import {StringField} from './builtin/StringField'

type ProofOfPresenceConfiguration = {
  required_authentication_level: string
}

export function ProofOfPresence({
  field,
  value,
  onValueChange,
  readOnly,
  metadata,
  errors,
  queryMetadata,
  sourceType,
  rulesetId,
  helpUrls,
  fieldRef,
  ruleParameters,
}: RegisteredRuleSchemaComponent) {
  if (field.type !== 'object' || !field.fields) {
    throw new Error('ProofOfPresence: field must be a configuration object')
  }

  const configuration =
    value && typeof value === 'object' && !Array.isArray(value) ? (value as ProofOfPresenceConfiguration) : undefined
  const enabled = configuration !== undefined
  const authenticationLevelField = field.fields.find(
    nestedField => nestedField.name === 'required_authentication_level',
  )

  if (!authenticationLevelField || authenticationLevelField.type !== 'string') {
    throw new Error('ProofOfPresence: schema is missing the required authentication level field')
  }

  const authenticationLevelValue = readOnly
    ? (authenticationLevelField.allowed_options?.find(
        option => option.value === configuration?.required_authentication_level,
      )?.display_name ?? configuration?.required_authentication_level)
    : configuration?.required_authentication_level

  const updateConfiguration = (updates: Partial<ProofOfPresenceConfiguration>) => {
    if (!configuration) return
    onValueChange({...configuration, ...updates})
  }

  return (
    <div>
      {!readOnly ? (
        <FormControl>
          <Checkbox
            checked={enabled}
            onChange={event => {
              if (!event.target.checked) {
                onValueChange(undefined)
                return
              }

              onValueChange({
                required_authentication_level: String(authenticationLevelField.default_value),
              })
            }}
            ref={fieldRef as React.Ref<HTMLInputElement> | undefined}
          />
          <FormControl.Label>
            {field.display_name}
            {field.beta && <BetaLabel className="ml-2" />}
          </FormControl.Label>
          <FormControl.Caption>{field.description}</FormControl.Caption>
        </FormControl>
      ) : enabled ? (
        <div>
          <span className="d-flex flex-items-center text-bold">{field.display_name}</span>
          <span className="d-block text-small color-fg-muted">{field.description}</span>
        </div>
      ) : null}
      {enabled ? (
        <div className="ml-4 mt-2 d-flex flex-column gap-3">
          <StringField
            sourceType={sourceType}
            rulesetId={rulesetId}
            helpUrls={helpUrls}
            field={authenticationLevelField}
            value={authenticationLevelValue}
            onValueChange={next => updateConfiguration({required_authentication_level: String(next)})}
            readOnly={readOnly}
            metadata={metadata}
            errors={errors}
            queryMetadata={queryMetadata}
            ruleParameters={ruleParameters}
          />
        </div>
      ) : null}
    </div>
  )
}
